import { memo, type JSX } from 'react';
import { Box, Link, SpaceBetween, TextContent } from '@amzn/awsui-components-core';
import ActionPanelBase from './ActionPanelBase';
import { PanelDivider } from './PanelDivider';
import { AboutStage, useAboutStagePanelViewModel } from './AboutStagePanelViewModel';

function DraftStageContent() {
  return (
    <SpaceBetween size={'xs'} direction={'vertical'}>
      <TextContent>
        <p>
          The Draft stage is where you write your first draft, including providing code, adding
          diagrams, and collaborating with coauthors.
        </p>
      </TextContent>

      <PanelDivider />

      <div>
        <Box variant="h4">What should authors do?</Box>
        <TextContent>
          <ol>
            <li>On the Authors tab, add coauthors to help you write your content.</li>
            <li>On the Content tab, complete all of the sections.</li>
            <li>
              If you have code, follow the instructions in {/* TODO - redirect to code tab  */}
              <Link variant="primary" href="#">
                Adding code
              </Link>
              .
            </li>
            <li>On the Metadata tab, confirm the content details.</li>
            <li>When you&apos;re done, start the quality check.</li>
          </ol>
        </TextContent>
      </div>

      <PanelDivider />

      <div>
        <Box variant="h4">What happens next</Box>
        <TextContent>
          <p>
            Your content will advance to the Quality Check stage, where AI scans review your content
            for quality, accuracy, and completeness.
          </p>
        </TextContent>
      </div>
    </SpaceBetween>
  );
}

function QualityCheckStageContent() {
  return (
    <SpaceBetween size={'xs'} direction={'vertical'}>
      <TextContent>
        <p>
          In the Quality Check stage, multiple AI-powered scans run automatically. The scans help
          you meet the quality bar before your content becomes accessible and discoverable to
          internal readers.
        </p>
      </TextContent>

      <PanelDivider />

      <div>
        <Box variant="h4">What should authors do?</Box>
        <TextContent>
          <ol>
            <li>Review the text changes and comments.</li>
            <li>Rerun the quality check as many times as you would like.</li>
            <li>
              Confirm that you are finished editing your content and that no text changes or
              comments remain.
            </li>
            <li>Submit the content for publisher review.</li>
          </ol>
        </TextContent>
      </div>

      <PanelDivider />

      <div>
        <Box variant="h4">What happens next</Box>
        <TextContent>
          <p>
            Your content will advance to the {/* TODO - redirect to publisher review docs */}
            <Link variant="primary" href="#">
              Publisher Review stage
            </Link>
            , where it will be reviewed by a domain expert. Your content will also be published
            internally with an &quot;unreviewed&quot; badge so that you can start sharing it
            immediately.
          </p>
        </TextContent>
      </div>
    </SpaceBetween>
  );
}

function PublisherReviewStageContent() {
  return (
    <SpaceBetween size={'xs'} direction={'vertical'}>
      <p>
        In the {/* TODO - redirect to publisher review docs */}
        <Link variant="primary" href="#">
          Publisher Review stage
        </Link>
        , APG bar raisers review your publication for quality, completeness, and accuracy. This
        human review ensures that your content meets a high quality bar.
      </p>

      <PanelDivider />

      <div>
        <Box variant="h4">What should authors do?</Box>
        <p>
          Wait for a bar raiser to complete their review. If it is not completed by the due date,
          you can open a {/* TODO - redirect to support ticket flow */}
          <Link variant="primary" href="#">
            support ticket
          </Link>
          .
        </p>
        <p>
          If a bar raiser rejects your publication, update your content, respond to the comments,
          and resubmit your content.
        </p>
      </div>

      <PanelDivider />

      <div>
        <Box variant="h4">What should bar raisers do?</Box>
        <p>
          Review the content according to the guidelines for{' '}
          {/* TODO - redirect to patterns docs */}
          <Link variant="primary" href="#">
            patterns
          </Link>{' '}
          and {/* TODO - redirect to guides docs */}
          <Link variant="primary" href="#">
            guides
          </Link>
          . Then, evaluate the publication and determine whether to approve or reject it.
        </p>
      </div>

      <PanelDivider />

      <div>
        <Box variant="h4">What happens next</Box>
        <p>
          After a bar raiser approves the content, it will be published internally. If the content
          is intended for an external audience, it will also be{' '}
          {/* TODO - redirect to external publication docs */}
          <Link variant="primary" href="#">
            considered for publication
          </Link>{' '}
          to the external APG website.
        </p>
      </div>
    </SpaceBetween>
  );
}

// TODO - replace with approved Published-stage copy (pending from product).
function PublishedStageContent() {
  return (
    <SpaceBetween size={'xs'} direction={'vertical'}>
      <p>Your content is published.</p>
    </SpaceBetween>
  );
}

const stageContent: Record<AboutStage, () => JSX.Element> = {
  draft: DraftStageContent,
  'quality-check': QualityCheckStageContent,
  'publisher-review': PublisherReviewStageContent,
  published: PublishedStageContent,
};

const stageTitle: Record<AboutStage, string> = {
  draft: 'About the draft stage',
  'quality-check': 'About the quality check',
  'publisher-review': 'About the publisher review',
  published: 'About the published stage',
};

/**
 * "About this stage" informational panel. Explains what the current workflow
 * stage means and what the author should do, switching content by stage.
 */
const AboutStagePanel = memo(function AboutStagePanel() {
  const { stage } = useAboutStagePanelViewModel();
  const StageContent = stageContent[stage];

  return (
    <ActionPanelBase title={stageTitle[stage]}>
      <StageContent />
    </ActionPanelBase>
  );
});

export default AboutStagePanel;
